import { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import QRCode from 'qrcode';
import { AudiqMark, AudiqWordmark, RuleBicolor } from '@audiq/ui';
import { useKiosk } from '../lib/kiosk';

export function DonePage() {
  const { t } = useTranslation();
  const { pickupCode } = useParams<{ pickupCode: string }>();
  const navigate = useNavigate();
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const { idleResetSeconds } = useKiosk();
  const [seconds, setSeconds] = useState(idleResetSeconds);

  // QR code rendering
  useEffect(() => {
    if (canvasRef.current && pickupCode) {
      void QRCode.toCanvas(canvasRef.current, pickupCode, {
        width: 220,
        margin: 1,
        color: {
          dark:
            getComputedStyle(document.documentElement).getPropertyValue('--ink').trim() ||
            '#1F2421',
          light: '#00000000',
        },
      });
    }
  }, [pickupCode]);

  // Auto-reset countdown (kiosk mode)
  useEffect(() => {
    const t = setInterval(() => {
      setSeconds((s) => {
        if (s <= 1) {
          sessionStorage.clear();
          navigate('/', { replace: true });
          return 0;
        }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(t);
  }, [navigate]);

  return (
    <div
      className="flex flex-1 flex-col items-center justify-center p-6 text-center"
      style={{ background: 'var(--surface)', color: 'var(--ink)' }}
    >
      <AudiqMark size={96} state="done" />

      <h1 className="serif mt-6 text-4xl" style={{ color: 'var(--ink)' }}>
        {t('done.title')}
      </h1>
      <p className="mt-2 max-w-sm" style={{ color: 'var(--mute)' }}>
        {t('done.subtitle')}
      </p>

      <RuleBicolor className="my-8 w-32" />

      <div
        className="mb-4 inline-block rounded-md border bg-[var(--paper)] px-6 py-4"
        style={{ borderColor: 'var(--line)' }}
      >
        <div
          className="audiq-font-display"
          style={{
            fontSize: 'clamp(2.5rem, 8vw, 4rem)',
            fontWeight: 700,
            letterSpacing: '0.08em',
            color: 'var(--ink)',
          }}
        >
          {pickupCode}
        </div>
      </div>

      <canvas ref={canvasRef} className="mb-6" aria-hidden="true" />

      <p className="max-w-sm text-sm" style={{ color: 'var(--mute)' }}>
        {t('done.explainer')}
      </p>

      <p className="mt-12 mono" style={{ color: 'var(--mute)' }}>
        {t('done.kioskAutoReset', { seconds })}
      </p>

      <div className="mt-4">
        <AudiqWordmark size="sm" />
      </div>
    </div>
  );
}
